import { useState } from 'react';
import StatusPicker, { type StatusOption } from './StatusPicker';
import AssignmentPicker from './AssignmentPicker';
import { getApiErrorMessage } from '../types/api';
import { useAuth } from '../context/AuthContext';
import type { CityMember } from '../types';

export interface HandoffPayload {
  assignee_user_id: number;
  assignment_context: string;
}

interface StateChangeWidgetProps {
  currentState: string;
  options: StatusOption[];
  onChangeState: (
    newState: string,
    comment: string,
    handoff?: HandoffPayload,
  ) => Promise<void>;
  isPending: boolean;
  /**
   * Optional: enable the "An Kollegen übergeben" block. When omitted the
   * widget keeps the original (pre-handoff) behavior — used for entities that
   * don't participate in the assignment system.
   */
  enableHandoff?: boolean;
  /** id of the user currently responsible for this entity, if any */
  currentAssigneeUserId?: number | null;
}

export default function StateChangeWidget({
  currentState,
  options,
  onChangeState,
  isPending,
  enableHandoff = false,
  currentAssigneeUserId = null,
}: StateChangeWidgetProps) {
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [newState, setNewState] = useState('');
  const [comment, setComment] = useState('');
  const [handoffEnabled, setHandoffEnabled] = useState(false);
  const [assigneeId, setAssigneeId] = useState<number | null>(null);
  const [assigneeMember, setAssigneeMember] = useState<CityMember | null>(null);
  const [assignmentContext, setAssignmentContext] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const current = options.find(o => o.key === currentState);
  const recommended = current?.recommended_transitions ?? [];

  const handoffToOtherUser =
    handoffEnabled && assigneeId !== null && user !== null && assigneeId !== user.id;
  const handoffContextMissing = handoffToOtherUser && !assignmentContext.trim();
  const handoffInvalid =
    handoffEnabled && (assigneeId === null || handoffContextMissing);

  const handleSubmit = async () => {
    setErrorMsg('');
    try {
      const handoff =
        handoffEnabled && assigneeId !== null
          ? {
              assignee_user_id: assigneeId,
              assignment_context: assignmentContext.trim(),
            }
          : undefined;
      await onChangeState(newState, comment, handoff);
      setShowForm(false);
      setNewState('');
      setComment('');
      setHandoffEnabled(false);
      setAssigneeId(null);
      setAssigneeMember(null);
      setAssignmentContext('');
    } catch (err) {
      setErrorMsg(getApiErrorMessage(err, 'Fehler beim Statuswechsel'));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setNewState('');
    setComment('');
    setHandoffEnabled(false);
    setAssigneeId(null);
    setAssigneeMember(null);
    setAssignmentContext('');
    setErrorMsg('');
  };

  const toggleHandoff = (next: boolean) => {
    setHandoffEnabled(next);
    if (next) {
      // Pre-select current assignee if one exists, else leave empty
      setAssigneeId(currentAssigneeUserId ?? null);
    } else {
      setAssigneeId(null);
      setAssigneeMember(null);
      setAssignmentContext('');
    }
  };

  if (!showForm) {
    return (
      <button
        onClick={() => setShowForm(true)}
        className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm"
      >
        Status ändern
      </button>
    );
  }

  return (
    <div className="bg-white border rounded-lg p-4 w-96 shadow-sm">
      <p className="text-xs text-gray-400 mb-3">Gedämpfte Optionen sind ungewöhnliche Übergänge</p>
      <StatusPicker
        options={options}
        currentKey={currentState}
        selected={newState}
        onSelect={setNewState}
      />
      {newState && !recommended.includes(newState) && (
        <p className="mt-2 text-xs text-amber-600">Ungewöhnlicher Übergang — bitte prüfen.</p>
      )}
      <textarea
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder="Kommentar (optional)"
        className="w-full border rounded px-2 py-1.5 text-sm mt-3"
        rows={2}
      />

      {enableHandoff && (
        <div className="mt-3 border-t pt-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={handoffEnabled}
              onChange={(e) => toggleHandoff(e.target.checked)}
            />
            An Kollegen übergeben
          </label>
          {handoffEnabled && (
            <div className="mt-2 space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Verantwortlich
                </label>
                <AssignmentPicker
                  value={assigneeId}
                  onChange={(uid, m) => {
                    setAssigneeId(uid);
                    setAssigneeMember(m);
                  }}
                  allowUnassign={false}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {handoffToOtherUser
                    ? `Hinweis für ${assigneeMember?.full_name ?? 'Empfänger'} (Pflicht)`
                    : 'Hinweis (optional)'}
                </label>
                <textarea
                  value={assignmentContext}
                  onChange={(e) => setAssignmentContext(e.target.value)}
                  placeholder="Was wurde erledigt, was wird vom Empfänger erwartet?"
                  rows={3}
                  className="w-full border rounded px-2 py-1.5 text-sm"
                />
              </div>
            </div>
          )}
        </div>
      )}

      {errorMsg && <p className="mt-1 text-xs text-red-600">{errorMsg}</p>}
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleSubmit}
          disabled={!newState || isPending || handoffInvalid}
          className="bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700 disabled:opacity-50"
        >
          {isPending ? '...' : handoffEnabled ? 'Übergeben & Status ändern' : 'Übernehmen'}
        </button>
        <button onClick={handleCancel} className="text-gray-500 hover:text-gray-700 text-sm">
          Abbrechen
        </button>
      </div>
    </div>
  );
}
